import { useId } from 'react';

/*
 * Why this is not a CSS blur:
 *
 * WebKit ignores CSS `filter` functions applied to SVG *child* elements
 * (https://bugs.webkit.org/show_bug.cgi?id=246106, open since 2022). Recharts
 * renders axis ticks as bare <text> nodes, so `style={{ filter: 'blur(6px)' }}`
 * silently did nothing in Safari and iOS Safari: every monetary value on the site
 * was blurred except the chart axes, which stayed perfectly readable in privacy
 * mode. MoneyDisplay is unaffected because it blurs an HTML <span>.
 *
 * Referencing a real SVG <filter> through the `filter` presentation attribute takes
 * WebKit's SVG-native filter path, which has worked since Safari 6.
 */

/**
 * CSS `blur(<n>px)` passes its argument straight through as the Gaussian standard
 * deviation, so this reproduces the previous `blur(6px)` exactly.
 */
const BLUR_STD_DEVIATION = 6;

/**
 * The blur filter that {@link PrivacyAwareYAxisTick} references.
 *
 * Render it inside the chart's own `<defs>` so the `url(#id)` reference never has to
 * cross an `<svg>` root — cross-root references break under a `<base href>` tag and
 * resolve to nothing when the defining SVG is `display: none`.
 */
export function PrivacyBlurFilter({ id }: { id: string }) {
    return (
        <filter
            id={id}
            // A Gaussian blur spreads roughly 3x its standard deviation in every
            // direction, but the default filter region is only -10%/120% of the text's
            // bounding box, which would clip the smudge back into a hard-edged
            // rectangle. The region is a percentage of that box and a one-character
            // tick has a very small one, hence the deliberately generous padding.
            x="-250%"
            y="-200%"
            width="600%"
            height="500%"
            // Filter primitives operate in linearRGB by default whereas CSS blur() uses
            // sRGB. Without this the axis blur reads noticeably paler than the blurred
            // figures MoneyDisplay renders next to it.
            colorInterpolationFilters="sRGB"
        >
            <feGaussianBlur stdDeviation={BLUR_STD_DEVIATION} />
        </filter>
    );
}

/**
 * Unique id for one chart's blur filter.
 *
 * Duplicate SVG ids are not merely untidy here: Safari resolves them to the *last*
 * matching definition in the document while other engines take the first, so two
 * charts on one page sharing a hardcoded id can distort each other's rendering.
 */
export function usePrivacyBlurFilterId(): string {
    return `privacy-blur-${useId()}`;
}

interface PrivacyAwareYAxisTickProps {
    /** Injected by Recharts when it clones this element for each tick. */
    x?: number;
    /** Injected by Recharts when it clones this element for each tick. */
    y?: number;
    /** Injected by Recharts when it clones this element for each tick. */
    payload?: { value: number };
    /** Formats the raw tick value for display. */
    formatValue: (value: number) => string;
    /** Whether this tick's value should be hidden. */
    blurred: boolean;
    /** Id of the {@link PrivacyBlurFilter} rendered in this chart's `<defs>`. */
    blurFilterId: string;
    fontSize?: number;
    fill?: string;
    textAnchor?: 'start' | 'middle' | 'end' | 'inherit';
}

/**
 * Y-axis tick label that hides its value in privacy mode, in every browser.
 *
 * Pair with a {@link PrivacyBlurFilter} of the same id inside the chart's `<defs>`.
 */
export function PrivacyAwareYAxisTick({
    x,
    y,
    payload,
    formatValue,
    blurred,
    blurFilterId,
    fontSize = 12,
    fill = '#6b7280',
    textAnchor = 'end',
}: PrivacyAwareYAxisTickProps) {
    if (!payload) return null;

    return (
        <text
            x={x}
            y={y}
            dy={4}
            fill={fill}
            fontSize={fontSize}
            textAnchor={textAnchor}
            filter={blurred ? `url(#${blurFilterId})` : undefined}
        >
            {formatValue(payload.value)}
        </text>
    );
}
